import get from 'lodash.get';
import React from 'react';
import { graphql } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import { Skeleton } from '@material-ui/lab';
import moment from 'moment-timezone';
import CollapsePanel from '../CollapsePanel/CollapsePanel';
import Button from '../BuildingBlocks/Button';
import Text from '../BuildingBlocks/Text';

import PropertyDetailContent from '../PropertyDetailContent';
import InstantPropertyBadge from '../../public/static/assets/svg/instantPropertyBadge';
import BookmarkSVG from '../../public/static/assets/svg/bookmarkSVG';
import { ErrorText } from '../../styles/PropertyDetails/styles';
import { getArray } from '../../lib/arrays';
import { QUERY_PROPERTY_DETAILS } from '../PropertyDetails/PropertyDetailsContainer/gql';
import BookNowContainer from '../PropertyDetails/BookNowContainer';
import RoomBookingCollection from '../RoomBookingCollection';
import PropertyAvailability from '../../constants/enums/PropertyAvailability';
import ORGANIZATION_PERMISSIONS from '../../constants/enums/OrganizationPermission';
import { hasOrganizationPermission } from '../../helpers/permissionHelper';

import {
  AccessibilityData,
  AddressTitle,
  AmenitySkeletonContainer,
  BookNowSkeletonContainer,
  Description,
  PropertyPaneContainer,
  LocationTitle,
  PaneAmenityList,
  PaneDetailsContainer,
  PropertyTitle,
  CloseContainer,
  CloseButtonContainer,
  CloseButton,
  StickyStart,
  Sticky,
  LoadingContainer,
  ImageCarouselContainer
} from './PropertyPane.styles';
import messages from './messages';
import PropertyPaneCarousel from './Carousel/ImageCarousel';
import IgnoreTranslation from '../Localize/ignoreTranslation';

function PropertyPane({
  showSelectedPropertyPane,
  selectedPropertyPane,
  openOnNew,
  handlePropertyPaneClose,
  data,
  auth,
  intl,
  bookingBypassReasonId
}) {
  const stickyNodes = {
    end: null,
    start: null,
    sticky: null
  };

  // functions to build property page with the required components
  const getDescription = () => {
    let description = get(data, 'property.description', '<p>null</p>');

    if (typeof description === 'string' && description.indexOf('<p>') === -1) {
      description = `<p>${description}</p>`;
    }

    return description;
  };

  function getImages() {
    // Gallery images
    const propertyImages = getArray(data, 'property.propertyImages', [])
      .sort((a, b) => a.priorityOrder - b.priorityOrder)
      .map(propertyImage => ({
        desc: propertyImage.description,
        id: propertyImage.id.toString(),
        src: propertyImage.href,
        title: propertyImage.title
      }));

    return <PropertyPaneCarousel images={propertyImages} />;
  }

  const renderDescription = ({ openText, closeText, description }) => {
    // Split the paragraphs into array items
    const paragraphs = description.split('</p>');
    paragraphs.pop();

    // Grab the first one to show initially on its own
    const [visible] = paragraphs;

    // Grab the rest that we want to show in the collapse panel, join the together so we can put them through RichText all at once
    const hidden = paragraphs.slice(1, paragraphs.length);
    const hiddenContent = hidden.join(' ');

    const mobileLayout = (
      <div>
        {PropertyDetailContent.renderRichText(visible)}
        {hidden.length > 0 && (
          <CollapsePanel openText={openText} closeText={closeText}>
            {PropertyDetailContent.renderRichText(hiddenContent)}
          </CollapsePanel>
        )}
      </div>
    );

    return mobileLayout;
  };

  const getAmenities = () =>
    getArray(data, 'property.allAmenities', []).map(amenity => ({
      icon: amenity.iconCode,
      id: amenity.id.toString(),
      title: amenity.name
    }));

  const getTopAmenities = () =>
    getArray(data, 'property.topAmenities', []).map(amenity => ({
      icon: amenity.iconCode,
      id: amenity.id.toString(),
      title: amenity.name
    }));

  const getAllAmenities = () => {
    const amenities = getAmenities();
    const topAmenities = getTopAmenities();

    return [...topAmenities, ...amenities];
  };

  const renderBookNow = (property, bookNowProps) => {
    const authenticated = get(auth, 'authenticated', false);

    const currentTimeEdmonton = moment().tz('America/Edmonton');
    const expiry = moment.tz('2025-01-01', 'America/Edmonton');
    const isBeforeExpiry = currentTimeEdmonton.isBefore(expiry);

    const shouldDisplayAlbertaTaxNotification = property?.address?.region === 'Alberta' && isBeforeExpiry;

    const {
      initiateBooking,
      checkin,
      checkout,
      numRooms,
      changeDates,
      changeNumRooms,
      changeRoomCount,
      getRoomCount,
      roomInputs,
      estimatedCost,
      currencyType,
      capacity,
      totalRoomCount,
      isRoomAvailable,
      rooms,
      nights,
      ratesLoading,
      propertyConnected,
      isBlockBooking,
      toggleBlockBooking,
      showBlockBookingToggle
    } = bookNowProps;
    const canCreateBooking = hasOrganizationPermission(auth, ORGANIZATION_PERMISSIONS.CREATE_BOOKING);
    const canCreateMultiGuestBooking = hasOrganizationPermission(
      auth,
      ORGANIZATION_PERMISSIONS.CREATE_MULTI_GUEST_BOOKING
    );
    // Only render if mounted in a browser not SSR
    // Render nothing if the property has no rooms
    if (rooms.length === 0) {
      return null;
    }
    const { timeZone, callForPricing } = property;
    let restrictPastCheckin = false;
    const isPastSelected = moment.tz(checkin, timeZone).isBefore(moment.tz(timeZone).startOf('day'));
    if (isPastSelected) {
      restrictPastCheckin = true;
    }

    return (
      // eslint-disable-next-line
      <>
        {authenticated && (
          <>
            <RoomBookingCollection.Container
              dates={{
                start: checkin,
                end: checkout
              }}
              numRooms={numRooms}
              onChangeDates={changeDates}
              onChangeRooms={changeNumRooms}
              title={intl.formatMessage(messages.selectRooms)}
              loading={ratesLoading}
              propertyPane
            >
              {restrictPastCheckin && (
                <div>
                  <ErrorText>{intl.formatMessage(messages.pastDateSelected, { message: timeZone })}</ErrorText>
                  <ErrorText>{intl.formatMessage(messages.pastDateCallToAction)}</ErrorText>
                </div>
              )}
              {rooms.map(room => {
                /**
                 * Temporary fix to show connected properties as Sold Out
                 * when room.availability returned from services as Undefined / Null
                 */
                if (propertyConnected) {
                  room.availability = room.availability || PropertyAvailability.SOLD_OUT;
                } else if (!room.rate.value) {
                  room.availability = PropertyAvailability.SOLD_OUT;
                }
                // end of temporary fix

                // identify if ceilingrate is available
                const ceilingIsAvailable = room.ceilingRate !== null;

                if (!auth.me || !auth.me.admin) {
                  return (
                    <RoomBookingCollection.Panel
                      loading={ratesLoading}
                      key={room.id}
                      roomId={room.id}
                      name={room.name}
                      totalRoomCount={totalRoomCount}
                      capacity={room.guestsPerRoom}
                      price={room.availability === PropertyAvailability.SOLD_OUT ? 'N/A' : room.rate.formatted}
                      availableRooms={room.numRoomsAvail}
                      availability={room.availability}
                      openText={intl.formatMessage(messages.hideRoomDetails)}
                      closeText={intl.formatMessage(messages.showRoomDetails)}
                      open={false}
                      currency={
                        // no ceiling check is necessary here
                        room.rate.value && room.availability !== PropertyAvailability.SOLD_OUT
                          ? intl.formatMessage(messages.perNightRate, {
                              name: room.rate.name
                            })
                          : ''
                      }
                      needsContactUsLink={!room.rate.value || false}
                      roomDescription={room.description}
                      onRoomsChange={
                        room.rate.value
                          ? value => {
                              changeRoomCount(room.id, value);
                            }
                          : () => {}
                      }
                      capacityText={intl.formatMessage(messages.capacity)}
                      startingText={
                        room.rate.value
                          ? intl.formatMessage(messages.averageRate)
                          : intl.formatMessage(messages.contactUs)
                      }
                      roomsValue={getRoomCount(room.id)}
                      roomInputs={roomInputs}
                      strikeoutPrice={ceilingIsAvailable ? null : room.strikeoutRate}
                      callForPricing={
                        callForPricing || room.callForPricing || room.rate === null || room.rate === undefined
                      }
                      nightlyRates={room.nightlyRates}
                      callForPricingLabel={intl.formatMessage(messages.callForAvailability)}
                      callForPricingPhoneNumber={intl.formatMessage(messages.contactUs)}
                      propertyPane
                      isAdmin={false}
                      propertyConnected={propertyConnected}
                      canCreateBooking={canCreateBooking}
                      canCreateMultiGuestBooking={canCreateMultiGuestBooking}
                    />
                  );
                }
                return (
                  <RoomBookingCollection.Panel
                    loading={ratesLoading}
                    key={room.id}
                    roomId={room.id}
                    name={room.name}
                    capacity={room.guestsPerRoom}
                    availableRooms={room.numRoomsAvail}
                    availability={room.availability}
                    price={room.rate.adminValues.formatted}
                    openText={intl.formatMessage(messages.hideRoomDetails)}
                    closeText={intl.formatMessage(messages.showRoomDetails)}
                    open={false}
                    currency={intl.formatMessage(messages.perNightRate, {
                      name: get(room, 'rate.adminValues.name')
                    })}
                    needsContactUsLink={false}
                    roomDescription={room.description}
                    onRoomsChange={value => {
                      changeRoomCount(room.id, value);
                    }}
                    capacityText={intl.formatMessage(messages.capacity)}
                    startingText={
                      room.rate.callForPricing
                        ? intl.formatMessage(messages.estimatedRate)
                        : intl.formatMessage(messages.averageRate)
                    }
                    roomsValue={getRoomCount(room.id)}
                    strikeoutPrice={ceilingIsAvailable ? null : room.strikeoutRate}
                    callForPricing={
                      callForPricing || room.rate.callForPricing || room.rate === null || room.rate === undefined
                    }
                    nightlyRates={room.nightlyRates}
                    callForPricingLabel={intl.formatMessage(messages.callForPricing)}
                    callForAvailabilityLabel={intl.formatMessage(messages.callForAvailability)}
                    callForPricingPhoneNumber={intl.formatMessage(messages.contactUs)}
                    bookingBypassReasonId={bookingBypassReasonId}
                    propertyPane
                    isAdmin
                    propertyConnected={propertyConnected}
                    canCreateBooking={canCreateBooking}
                    canCreateMultiGuestBooking={canCreateMultiGuestBooking}
                  />
                );
              })}
            </RoomBookingCollection.Container>

            {/*
              The booking summary will start to stick when the bottom of the window
              reaches this element it also leaves some extra space in the page so
              the contents of the page don't jump around when the nav becomes sticky.
            */}
            <StickyStart
              ref={node => {
                stickyNodes.start = node;
              }}
            >
              {/* Sticks and unsticks the booking summary. */}
              <Sticky
                ref={node => {
                  stickyNodes.sticky = node;
                }}
              >
                <RoomBookingCollection.Summary
                  totalPrice={intl
                    .formatNumber(estimatedCost || 0, {
                      style: 'currency',
                      currency: currencyType,
                      currencyDisplay: 'symbol'
                    })
                    .replace(/us|ca|/gi, '')}
                  capacity={capacity}
                  numRooms={numRooms}
                  isRoomAvailable={isRoomAvailable}
                  rooms={totalRoomCount}
                  nights={nights}
                  onClick={initiateBooking}
                  bookingBypassReasonId={bookingBypassReasonId}
                  restrictPastCheckin={restrictPastCheckin}
                  propertyPane
                  isBlockBooking={isBlockBooking}
                  showBlockBookingToggle={showBlockBookingToggle}
                  toggleBlockBooking={toggleBlockBooking}
                  labels={{
                    bookingButton: intl.formatMessage(messages.bookNow).toUpperCase(),
                    capacity: intl.formatMessage(messages.capacity),
                    template: 'You have {rooms} {roomLabel} selected for {nights} {nightLabel}',
                    estimatedCost: intl.formatMessage(messages.estimatedCost),
                    currency: currencyType,
                    mobileCurrency: currencyType,
                    singularRoom: intl.formatMessage(messages.room),
                    multipleRooms: intl.formatMessage(messages.roomPlural),
                    singularNight: intl.formatMessage(messages.night),
                    multipleNights: intl.formatMessage(messages.nightPlural),
                    maxOccupancyNote: intl.formatMessage(messages.maxOccupancyNote),
                    blockBooking: intl.formatMessage(messages.blockBookingText),
                    permissionDenied: intl.formatMessage(messages.permissionDenied),
                    restrictedRooms: intl.formatMessage(messages.restrictedRooms)
                  }}
                  canCreateBooking={canCreateBooking}
                  canCreateMultiGuestBooking={canCreateMultiGuestBooking}
                  shouldDisplayAlbertaTaxNotification={shouldDisplayAlbertaTaxNotification}
                />
              </Sticky>
            </StickyStart>
          </>
        )}
      </>
    );
  };

  const handleViewDetailsButtonClick = () => openOnNew(selectedPropertyPane.slug, selectedPropertyPane);

  const renderBadges = ({ connected, favourite }) => (
    <AccessibilityData>
      {favourite && (
        <div className="bookmark">
          <BookmarkSVG onClick={() => {}} selected={favourite} isStatic size="large" />
          <Text.CopyBody className="banner-description">{intl.formatMessage(messages.bookMarkText)}</Text.CopyBody>
        </div>
      )}

      {connected && (
        <div className="instantProperty">
          <InstantPropertyBadge enabled={connected} size="large" />
          <div>
            <Text.CopyBody weight="bold" className="banner-description" noMargins>
              {intl.formatMessage(messages.instantBookingProperty)}
            </Text.CopyBody>
            <Text.CopyBody className="banner-description" noMargins>
              {intl.formatMessage(messages.instantBookingPropertyHelper)}
            </Text.CopyBody>
          </div>
        </div>
      )}
    </AccessibilityData>
  );

  const address = {
    address: get(data, 'property.address.address'),
    city: get(data, 'property.address.city'),
    region: get(data, 'property.address.region'),
    postalCode: get(data, 'property.address.postalCode'),
    country: get(data, 'property.address.country')
  };

  const location = `${get(data, 'property.address.city')}, ${get(data, 'property.address.region')}`;
  const name = get(data, 'property.name');
  const description = getDescription();
  const amenities = getAllAmenities();
  const openText = intl.formatMessage(messages.seeMore);
  const closeText = intl.formatMessage(messages.seeLess);

  const property = get(data, 'property');

  return (
    // eslint-disable-next-line
    <>
      {showSelectedPropertyPane && selectedPropertyPane && (
        <PropertyPaneContainer>
          {data.loading && (
            <LoadingContainer>
              <div>
                <Skeleton variant="rect" width="100%" height={250} style={{ marginBottom: '10px' }} />
                <Skeleton variant="text" width="30%" height={25} style={{ marginBottom: '-5px' }} />
                <Skeleton variant="text" width="75%" height={40} style={{ marginBottom: '-5px' }} />
                <Skeleton variant="text" width="50%" height={25} style={{ marginBottom: '10px' }} />

                {Array.from(Array(6)).map(index => (
                  <Skeleton key={index} variant="text" width="100%" height={20} style={{ marginBottom: '-5px' }} />
                ))}

                <Skeleton variant="text" width="25%" height={40} style={{ marginBottom: '-5px' }} />

                <AmenitySkeletonContainer>
                  {Array.from(Array(3)).map(index => (
                    <Skeleton key={index} variant="text" width="33%" height={45} style={{ marginRight: '10px' }} />
                  ))}
                </AmenitySkeletonContainer>

                <AmenitySkeletonContainer>
                  {Array.from(Array(3)).map(index => (
                    <Skeleton key={index} variant="text" width="33%" height={45} style={{ marginRight: '10px' }} />
                  ))}
                </AmenitySkeletonContainer>

                <Skeleton variant="text" width="57%" height={80} />
                <BookNowSkeletonContainer>
                  <Skeleton variant="text" width="40%" height={40} />
                  {Array.from(Array(6)).map(index => (
                    <Skeleton key={index} variant="rect" width="100%" height={90} style={{ marginBottom: 15 }} />
                  ))}
                </BookNowSkeletonContainer>
              </div>
            </LoadingContainer>
          )}
          {!data.loading && (
            <>
              <CloseContainer>
                <CloseButtonContainer>
                  <CloseButton
                    selectedIcon="closeModal"
                    iconColor="white"
                    onClick={handlePropertyPaneClose}
                    style={{ position: 'absolute', cursor: 'pointer' }}
                  />
                </CloseButtonContainer>
              </CloseContainer>
              <div style={{ height: '100%' }}>
                <PaneDetailsContainer>
                  <ImageCarouselContainer>{getImages()}</ImageCarouselContainer>
                  <IgnoreTranslation>
                    <LocationTitle>{`${location}`}</LocationTitle>
                    <PropertyTitle>{`${name}`}</PropertyTitle>
                    <AddressTitle>{`${address.address}, ${location}`}</AddressTitle>
                  </IgnoreTranslation>
                  <Description>{renderDescription({ openText, closeText, description })}</Description>
                  <div style={{ margin: '10px 0' }}>
                    <Button
                      onClick={() => {
                        handleViewDetailsButtonClick();
                      }}
                      size="sm"
                      theme="ghost"
                    >
                      View Details
                    </Button>
                  </div>
                  {renderBadges(selectedPropertyPane)}
                  {amenities && amenities.length > 0 && (
                    <PaneAmenityList
                      amenities={amenities}
                      title={intl.formatMessage(messages.amenities)}
                      openText={intl.formatMessage(messages.seeMore)}
                      closeText={intl.formatMessage(messages.seeLess)}
                    />
                  )}

                  <BookNowContainer
                    slug={selectedPropertyPane.slug}
                    propertyTimeZone={selectedPropertyPane.timeZone}
                    bookingBypassReasonId={bookingBypassReasonId}
                  >
                    {bookNowProps =>
                      renderBookNow(property, bookNowProps) ? (
                        renderBookNow(property, bookNowProps)
                      ) : (
                        <Skeleton variant="rect" animation="wave" width="100%" height={600} />
                      )
                    }
                  </BookNowContainer>
                </PaneDetailsContainer>
              </div>
            </>
          )}
        </PropertyPaneContainer>
      )}
    </>
  );
}

export default compose(
  graphql(QUERY_PROPERTY_DETAILS, {
    skip: props => !props.selectedPropertyPane?.slug,
    options: props => ({
      variables: {
        slug: props.selectedPropertyPane?.slug
      }
    })
  })
)(PropertyPane);
